import { create } from 'zustand';
import { User } from '../types';
import apiClient from '../api/client';

/**
 * Auth State Store - NO tokens stored
 * CRITICAL: Frontend NEVER stores or accesses tokens
 * Validates: Requirements 4.1
 */

interface AuthState {
  // State
  isAuthenticated: boolean;
  user: User | null;
  isLoading: boolean;
  error: string | null;
  // NO accessToken or refreshToken fields - CRITICAL
  
  // Actions
  setUser: (user: User | null) => void;
  setLoading: (loading: boolean) => void;
  setError: (error: string | null) => void;
  logout: () => void;
  clearState: () => void;
  
  // Async actions
  checkSession: () => Promise<void>;
  login: (email: string, password: string) => Promise<boolean>;
  performLogout: () => Promise<void>;
}

export const useAuthStore = create<AuthState>((set, get) => ({
  // Initial state
  isAuthenticated: false,
  user: null,
  isLoading: true,
  error: null,
  
  // Synchronous actions
  setUser: (user) => set({ 
    user, 
    isAuthenticated: !!user,
    isLoading: false,
    error: null,
  }),
  
  setLoading: (isLoading) => set({ isLoading }),
  
  setError: (error) => set({ error }),
  
  logout: () => set({ 
    user: null, 
    isAuthenticated: false,
    isLoading: false,
    error: null,
  }),
  
  clearState: () => set({
    user: null,
    isAuthenticated: false,
    isLoading: false,
    error: null,
  }),
  
  // Async actions
  
  /**
   * Check current session status with BFF
   * Called on app initialization to restore auth state
   */
  checkSession: async () => {
    console.log('[AuthStore] checkSession called');
    set({ isLoading: true, error: null });

    try {
      console.log('[AuthStore] Fetching session from /auth/session');
      const response = await apiClient.get<{ user: User }>('/auth/session');
      console.log('[AuthStore] Session check successful, user:', response.data.user);
      set({
        user: response.data.user,
        isAuthenticated: true,
        isLoading: false,
        error: null,
      });
    } catch (error) {
      // Session check failed - user is not authenticated
      // This is expected for unauthenticated users, not an error
      console.log('[AuthStore] Session check failed (expected for unauthenticated users):', error);
      set({
        user: null,
        isAuthenticated: false,
        isLoading: false,
        error: null,
      });
    }
  },
  
  /**
   * Login with email and password
   * Returns true on success, false on failure
   */
  login: async (email: string, password: string): Promise<boolean> => {
    console.log('[AuthStore] Login called with email:', email);
    set({ isLoading: true, error: null });

    try {
      console.log('[AuthStore] Making login request to /auth/login');
      const response = await apiClient.post<{ user: User }>('/auth/login', {
        email,
        password,
        organizationId: 'cmj97cd5000004doltxb1ahls', // LLM-ELITE organization
      });

      console.log('[AuthStore] Login successful, user:', response.data.user);
      set({
        user: response.data.user,
        isAuthenticated: true,
        isLoading: false,
        error: null,
      });

      // Broadcast login event to other tabs
      broadcastAuthEvent('login');

      return true;
    } catch (error: unknown) {
      console.error('[AuthStore] Login failed with error:', error);
      const errorMessage = getErrorMessage(error);
      console.error('[AuthStore] Error message:', errorMessage);
      set({
        user: null,
        isAuthenticated: false,
        isLoading: false,
        error: errorMessage,
      });
      return false;
    }
  },
  
  /**
   * Perform logout - calls BFF to invalidate session
   */
  performLogout: async () => {
    const { clearState } = get();
    
    try {
      await apiClient.post('/auth/logout');
    } catch (error) {
      // Logout failed on server, but we still clear local state
      console.error('Logout request failed:', error);
    }
    
    // Clear local state
    clearState();
    
    // Broadcast logout event to other tabs
    broadcastAuthEvent('logout');
  },
}));

/**
 * Broadcast auth event to other tabs
 */
function broadcastAuthEvent(type: 'login' | 'logout'): void {
  try {
    const authChannel = new BroadcastChannel('auth');
    authChannel.postMessage({ type });
    authChannel.close();
  } catch {
    // BroadcastChannel not supported, use localStorage fallback
    localStorage.setItem('auth-event', JSON.stringify({ 
      type, 
      timestamp: Date.now() 
    }));
  }
}

/**
 * Extract error message from error object
 */
function getErrorMessage(error: unknown): string {
  if (error && typeof error === 'object' && 'response' in error) {
    const axiosError = error as { response?: { data?: { message?: string } } };
    if (axiosError.response?.data?.message) {
      return axiosError.response.data.message;
    }
  }
  return 'An error occurred. Please try again.';
}
